import { useMemo } from "react";
import { Paper, Title, Box, Text, Group, Stack, UnstyledButton, Progress } from "@mantine/core";
import { PieChart } from "@mantine/charts";
import { IconChevronRight } from "@tabler/icons-react";
import {
  sourceColour,
  sourceCssColour,
  sourceLabel,
  originTitle,
  forChannel,
  rollupLabels,
} from "./trafficDisplay";
import rowStyles from "./inspectRow.module.css";

/**
 * Where arrivals came from, by channel — and a way into the exact origin.
 *
 * Takes server-aggregated SESSIONS with first-touch attribution: one visit is counted once, in the
 * channel it arrived from. The previous version counted raw EVENTS by traffic source in the
 * browser, which made chatty channels look like big ones, disagreed with the digest by design,
 * and — because internal navigation classifies as "direct" — let direct swallow the mix.
 *
 * The pie alone was as far as you could get. The ranked list is the drill-in: each channel opens
 * the hosts, campaigns, terms and countries that make it up.
 */

export default function TrafficSources({
  sources,
  origins,
  isLoading,
  compact = false,
  onSelectChannel,
}) {
  const data = useMemo(() => {
    if (!sources?.length) return [];
    return sources
      .filter((s) => s.sessions > 0)
      .map((s) => ({
        source: s.source,
        name: sourceLabel(s.source),
        value: s.sessions,
        color: sourceColour(s.source),
      }));
  }, [sources]);

  const totalSessions = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

  if (isLoading) {
    return (
      <Paper p="md" withBorder={!compact}>
        {!compact && <Title order={4}>Traffic Sources</Title>}
        <Box h={compact ? 180 : 250} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text c="dimmed">Loading...</Text>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder={!compact}>
      {!compact && (
        <Group justify="space-between" mb="xs" align="flex-start">
          <div>
            <Title order={4}>Traffic Sources</Title>
            <Text size="xs" c="dimmed">
              Sessions by first arrival. Click a channel to see the exact source.
            </Text>
          </div>
        </Group>
      )}
      {compact ? (
        <Box h={180}>
          <SourcePie data={data} size={150} />
        </Box>
      ) : (
        <Group align="flex-start" wrap="wrap" gap="lg">
          <Box h={220} w={220} style={{ flex: "0 0 220px" }}>
            <SourcePie
              data={data}
              size={180}
              onSelect={onSelectChannel}
            />
          </Box>
          <Stack gap={4} style={{ flex: 1, minWidth: 240 }}>
            {data.length > 0 ? (
              data.map((row) => (
                <ChannelRow
                  key={row.source}
                  row={row}
                  total={totalSessions}
                  topOrigin={topOriginFor(row.source, origins)}
                  onSelect={onSelectChannel}
                />
              ))
            ) : (
              <Box h={200} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Text c="dimmed">No data available</Text>
              </Box>
            )}
          </Stack>
        </Group>
      )}
      {totalSessions > 0 && compact && (
        <Box mt="md">
          <Text size="sm" c="dimmed" ta="center">
            {totalSessions.toLocaleString()} session{totalSessions === 1 ? "" : "s"}
          </Text>
        </Box>
      )}
    </Paper>
  );
}

function SourcePie({ data, size, onSelect }) {
  if (!data.length) {
    return (
      <Box h="100%" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Text c="dimmed">No data available</Text>
      </Box>
    );
  }

  return (
    <PieChart
      data={data}
      withLabelsLine
      labelsPosition="outside"
      labelsType="percent"
      withLabels
      withTooltip
      tooltipDataSource="segment"
      size={size}
      pieProps={
        onSelect
          ? {
              style: { cursor: "pointer" },
              onClick: (slice) => {
                const source = slice?.source ?? slice?.payload?.source;
                if (source) onSelect(source);
              },
            }
          : undefined
      }
    />
  );
}

function ChannelRow({ row, total, topOrigin, onSelect }) {
  const pct = total > 0 ? (row.value / total) * 100 : 0;
  const clickable = typeof onSelect === "function";

  const body = (
    <Group gap="sm" wrap="nowrap" py={6} px={4}>
      <Box
        w={8}
        h={8}
        style={{ borderRadius: 999, background: sourceCssColour(row.source), flexShrink: 0 }}
      />
      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text size="sm" fw={500}>
            {row.name}
          </Text>
          <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
            {row.value.toLocaleString()}
          </Text>
        </Group>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text size="xs" c="dimmed" truncate>
            {topOrigin
              ? `${originTitle(topOrigin.label)}${topOrigin.others > 0 ? ` + ${topOrigin.others}` : ""}`
              : "\u00a0"}
          </Text>
          <Text size="xs" c="dimmed">
            {pct.toFixed(0)}%
          </Text>
        </Group>
        <Progress
          value={pct}
          size="xs"
          color={sourceColour(row.source)}
          mt={4}
          radius="xs"
        />
      </Stack>
      {clickable && (
        <IconChevronRight size={14} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
      )}
    </Group>
  );

  if (!clickable) return body;

  return (
    <UnstyledButton
      onClick={() => onSelect(row.source)}
      w="100%"
      className={rowStyles.row}
      aria-label={`Inspect ${row.name} traffic`}
    >
      {body}
    </UnstyledButton>
  );
}

function topOriginFor(source, origins) {
  const rows = rollupLabels(forChannel(origins, source), "origin");
  if (!rows.length) return null;
  return { label: rows[0].label, others: rows.length - 1 };
}
